/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-plusplus */
import _ from 'lodash';
import {
  clearFleetDetailSelectedFin, setFinChangesStateReducer,
} from '../../models/checkerDetailsModel/checkerDetailsSlice';
import { updateCheckerFinStatusDetails, loadCheckerDetails } from '../../models/checkerDetailsModel/checkerDetailsThunk';
import { addToast, removeToast } from '../../models/toastModel/toastSlice';
import {
  FIN_STATUS_MAPPER_FOR_API, DATA_SOURCE_COMPARISON_VALUE, FIN_UPDATE_SUCCESS, FIN_UPDATE_FAILURE, TOAST_TIMEOUT,
  DEFAULT_OFFSET,
} from '../../utils/constants';
import { columns as checkerDetailscolumns } from '../CheckerDetailsTable/CheckerDetailsTableStructure';
import { getFromLocalStore } from '../../utils/commonMethods/generalMethods';
import { getFilteredCheckerDetailsColumns } from '../../utils/commonUtil';
import { formatPartNumber } from '../../utils/commonMethods/generalMethodsExtended';

/**
 * Builds payload conditions for checker details, handling "No FIN" logic.
 * @param checkerDataTableState The state object containing checker detail filters.
 * @returns An object with payload conditions and the filtered selectedFin array.
 */
export function buildPayloadConditions(checkerDataTableState: any) {
  let selectedFin = checkerDataTableState.checkerDetailFiltersSelected.selected_fin || [];
  let noFin = false;
  /* istanbul ignore next */
  if (selectedFin.includes('No FIN')) {
    noFin = true;
    selectedFin = selectedFin.filter((fin: string) => fin !== 'No FIN');
  }

  const payloadConditions: any = {
    id_aircraft_registration: checkerDataTableState.checkerDetailFiltersSelected.selected_msn,
    aircraft_type: checkerDataTableState.checkerDetailFiltersSelected.aircraft_type,
    ac_subtype: checkerDataTableState.checkerDetailFiltersSelected.aircraft_subtype,
    data_source: [DATA_SOURCE_COMPARISON_VALUE.PAYLOAD_VALUE],
    ata: checkerDataTableState.checkerDetailFiltersSelected.selected_ata,
  };
  if (noFin) {
    payloadConditions.no_fin = ['true'];
  }
  if (selectedFin.length > 0) {
    payloadConditions.fleet_fin = selectedFin;
  }
  return payloadConditions;
}
// Write back status submit handler
export const handleFinStatusSubmit = async ({
  checkerDetailFiltersSelected, checkerDataTableState, finChangesState, CheckerFinEditModalPopupDispatcher, isClearSelection, featureToggle, applyAllToggleState,
}: any) => {
  let toastValForFinUpdatedRes: ToastType = {
    toastLocation: 'api/checker/update_fin_detail',
    isToastShown: true,
    toastMessage: '',
    toastVariantType: 'success',
    customToastIcon: '',
  };

  const writebackStatusPayload = {
    operator_code_icao: new Array(checkerDetailFiltersSelected.operator_name),
    data: [],
  };

  // 1. If toggle is OFF, just use the changes as they are.
  // 2. If toggle is ON, we use the specific card's values IF it was edited.
  //    Otherwise, we fall back to the "Master" (Source) values.

  const sourceFinStatus = applyAllToggleState
    ? [...finChangesState].reverse().find((finVal: CheckerSelectedFinDataType) => {
      return !!finVal.fin_status;
    })
    : undefined;

  const sourceFinComment = applyAllToggleState
    ? [...finChangesState].reverse().find((finVal: CheckerSelectedFinDataType) => {
      return !!finVal.comment;
    })
    : undefined;

  const baseFinChangesState = finChangesState.map((finVal: CheckerSelectedFinDataType) => {
    if (!applyAllToggleState) return finVal;

    // Logic: If the user specifically edited this card, its fin_status
    // will already be in the state. If they didn't, we apply the master/source.
    return {
      ...finVal,
      // Priority: 1. Current card value (if it exists) -> 2. Master value -> 3. Original value
      fin_status: finVal.fin_status || sourceFinStatus?.fin_status || '',
      comment: finVal.comment || sourceFinComment?.comment || '',
    };
  });
  const finChangesArray = baseFinChangesState.map((finVal: CheckerSelectedFinDataType) => {
    return {
      id_aircraft_registration: finVal.id_aircraft_registration,
      fleet_fin: finVal.fleet_fin,
      fin_status: FIN_STATUS_MAPPER_FOR_API[finVal.fin_status] ? FIN_STATUS_MAPPER_FOR_API[finVal.fin_status] : finVal.fin_status,
      comment: finVal.comment,
      part_number: formatPartNumber(finVal.part_number),
      pnr_count: (() => {
        if (Array.isArray(finVal.pnr_count)) {
          return finVal.pnr_count.length > 0 ? String(finVal.pnr_count[0]) : '1';
        }
        return finVal.pnr_count !== null && finVal.pnr_count !== undefined ? String(finVal.pnr_count) : '1';
      })(),
    };
  });

  writebackStatusPayload.data = finChangesArray;
  const response = await CheckerFinEditModalPopupDispatcher(updateCheckerFinStatusDetails(writebackStatusPayload)());
  if (!('error' in response)) {
    const payloadConditions = buildPayloadConditions(checkerDataTableState);
    const conditionsRes = Object.fromEntries(
      Object.entries(payloadConditions).filter(([, v]: [string, any]) => !_.isEmpty(v) && !(v.length === 1 && v[0] === '')),
    );
    let payload = {
      operator_code_icao: new Array(checkerDetailFiltersSelected.operator_name),
      conditions: conditionsRes,
      distinct: true,
      columns: featureToggle ? checkerDetailscolumns : getFilteredCheckerDetailsColumns(checkerDetailscolumns),
    };
    if (Object.keys(checkerDataTableState.FilterPayload).length > 0) { payload = checkerDataTableState.FilterPayload; }
    CheckerFinEditModalPopupDispatcher(loadCheckerDetails({
      ...payload,
      offset: Number(DEFAULT_OFFSET),
      limit: Number(getFromLocalStore('apiPaginationSize')),
    }, {})());
    // Show success toast if fin edited
    toastValForFinUpdatedRes = {
      ...toastValForFinUpdatedRes, toastMessage: FIN_UPDATE_SUCCESS, toastVariantType: 'success', isToastShown: true,
    };
    CheckerFinEditModalPopupDispatcher(addToast({
      ...toastValForFinUpdatedRes,
    }));
    CheckerFinEditModalPopupDispatcher(clearFleetDetailSelectedFin(!isClearSelection));
    CheckerFinEditModalPopupDispatcher(setFinChangesStateReducer([]));
  } else {
    toastValForFinUpdatedRes = {
      ...toastValForFinUpdatedRes, toastVariantType: 'error', toastMessage: FIN_UPDATE_FAILURE, isToastShown: true,
    };
    // if failed to update fin status show toast
    CheckerFinEditModalPopupDispatcher(addToast({
      ...toastValForFinUpdatedRes,
    }));
  }
  setTimeout(() => {
    CheckerFinEditModalPopupDispatcher(
      removeToast({
        ...toastValForFinUpdatedRes,
      }),
    );
  }, TOAST_TIMEOUT);
};

// To get the previous history
export const getHistory = (historyRecords: any) => {
  const transition = [];
  for (let i = 0; i < historyRecords.length - 1; i++) {
    const currentStatus = historyRecords[i].fin_status;
    const partNumber = historyRecords[i].part_number;
    const Comments = historyRecords[i].comment;
    const timeStamp = historyRecords[i].revised_timestamp;
    const updatedBy = historyRecords[i].updated_by;
    const previousStatus = historyRecords[i + 1].fin_status;
    transition.push({
      previousStatus, partNumber, currentStatus, updatedBy, timeStamp, Comments,
    });
  }
  return transition;
};

// To handle onchange of comments section
export const handleOnchangeComment = (nextValue: string, index: number, _state: any, selectedFinValues: any, _toggle: any, args: any) => {
  const { checkerDataTableState, CheckerFinEditModalPopupDispatcher } = args;
  const changes = [...(checkerDataTableState.finChangesState || [])];

  const existingIndex = changes.findIndex((c: any) => c.fleet_fin === selectedFinValues.fleet_fin
    && c.id_aircraft_registration === selectedFinValues.id_aircraft_registration);

  if (existingIndex > -1) {
    // Update ONLY this specific card's comment
    changes[existingIndex] = { ...changes[existingIndex], comment: nextValue };
  } else {
    changes.push({ ...selectedFinValues, comment: nextValue, fin_status: '' });
  }

  CheckerFinEditModalPopupDispatcher(setFinChangesStateReducer(changes));
};
